import React from 'react';

import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { mediaSelector } from 'helpers/style-utils';

import kebabCase from 'lodash/kebabCase';

import Link from 'next/link';

import { useTranslation } from 'lib/i18n';

import { PageNames } from './TosPage';

const StyledTosNavigation = styled.nav`
  width: 280px;
  margin-right: 30px;
  ${mediaSelector.small} {
    display: none;
  }
`;

const Sticky = styled.div`
  position: sticky;
  top: 10px;
`;

const PageNav = styled.ul`
  list-style-type: none;
  border-top: 1px solid ${p => p.theme.currentTheme.border};
  margin: 0;
  padding: 5px 0 0 0;
`;

const PageNavLink = styled.a`
  cursor: pointer;
  display: block;
  padding: 13px 15px 11px 16px;
  font-size: 1.8rem;
  line-height: 2rem;
  color: ${p => p.theme.currentTheme.primary};
  &:hover {
    text-decoration: underline;
  }
`;

const HeadingNav = styled.ul`
  list-style-type: none;
  margin: 0 0 20px 0;
  padding: 0;
`;

// prettier-ignore
const HeadingNavLink = styled.a<{ active?: boolean }>`
  cursor: pointer;
  display: block;
  padding: 13px 15px 11px 16px;
  border-radius: 10px;
  font-size: 1.8rem;
  line-height: 2rem;
  &:hover {
    background: ${p => p.theme.currentTheme.hover};
  }
  ${p => p.active && `
    background: ${p.theme.currentTheme.active};
  `}
`;

export const headingId = (children: JSX.Element[]) => kebabCase(children?.[0]?.props?.value);

const HeadingNavItem: React.FC<{ level: number; children: JSX.Element[] }> = ({
  level,
  children,
}) => {
  if (level >= 2) {
    const href = `#${headingId(children)}`;
    return (
      <li>
        <HeadingNavLink href={href}>{children}</HeadingNavLink>
      </li>
    );
  }
  return null;
};

type Props = {
  pageName: PageNames;
};

const TosNavigation: React.FC<Props> = ({ pageName }) => {
  const { t } = useTranslation('tos');
  return (
    <StyledTosNavigation>
      <Sticky>
        <HeadingNav>
          <ReactMarkdown
            source={t(`${pageName}.text`)}
            includeNodeIndex={true}
            allowedTypes={['root', 'heading', 'text']}
            renderers={{
              heading: HeadingNavItem,
            }}
          />
        </HeadingNav>
        <PageNav>
          {pageName !== 'privacy' && (
            <li>
              <Link href="/tos?pageName=privacy" as="/tos/privacy">
                <PageNavLink>Privacy Policy</PageNavLink>
              </Link>
            </li>
          )}
          {pageName !== 'tos' && (
            <li>
              <Link href="/tos" as="/tos">
                <PageNavLink>Terms of service</PageNavLink>
              </Link>
            </li>
          )}
        </PageNav>
      </Sticky>
    </StyledTosNavigation>
  );
};

export default TosNavigation;
